import { Head, Link, router, usePoll } from "@inertiajs/react";
import {
  AlertTriangle,
  ArrowLeft,
  Building2,
  Calendar,
  Check,
  Clock,
  DollarSign,
  ExternalLink,
  FileText,
  Loader2,
  MessageSquare,
  Package,
  Plus,
} from "lucide-react";
import { useMemo, useState } from "react";

import CurrencyFormatHint from "@/components/currency-format-hint";
import InlineEditableCombobox from "@/components/inline-editable-combobox";
import InlineEditableField from "@/components/inline-editable-field";
import QuoteBuyerAnalysis from "@/components/quote-buyer-analysis";
import NewQuoteItemModal from "@/components/quote-items/NewQuoteItemModal";
import QuoteItemsGrid from "@/components/quote-items-grid";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { formatCurrency } from '@/helpers/currency-formatter';
import AppLayout from "@/layouts/app-layout";
import {
  purchaseRequestPath,
  purchaseRequestsPath,
  quotePath,
} from "@/routes";
import type {
  BreadcrumbItem,
  PurchaseRequest,
  Quote,
  QuoteBuyerAnalysis as QuoteBuyerAnalysisType,
  QuoteItem,
  RequestItem,
  Supplier,
} from "@/types";
import { isPdfUrl } from "@/utils/file";

interface QuoteShowProps {
  quote: Quote & {
    supplier: {
      id: number;
      name: string;
    };
    purchase_request: {
      id: number;
      request_number: string;
    };
    total_in_ars: number;
  };
  quote_items: (QuoteItem & {
    unit_price_in_ars: number;
    product?: {
      id: number;
      normalized_name: string;
    };
    request_item?: {
      id: number;
      description: string;
      item_number: number;
    };
  })[];
  purchase_request?: PurchaseRequest & {
    sector: {
      id: number;
      name: string;
    };
  };
  buyer_analysis?: QuoteBuyerAnalysisType;
  available_request_items?: RequestItem[];
  suppliers?: Supplier[];
}

const getConfidenceColor = (confidence: number | undefined) => {
  if (!confidence) return { bg: "bg-gray-200", text: "text-gray-600" };
  if (confidence < 0.4) return { bg: "bg-red-200", text: "text-red-800" };
  if (confidence < 0.6) return { bg: "bg-amber-200", text: "text-amber-800" };
  return { bg: "bg-green-200", text: "text-green-800" };
};

const getConfidenceLabel = (confidence: number | undefined) => {
  if (!confidence) return "Sin datos";
  if (confidence < 0.4) return "Muy baja";
  if (confidence < 0.6) return "Baja";
  if (confidence < 0.8) return "Media";
  return "Alta";
};

export default function QuoteShow({
  quote,
  quote_items,
  purchase_request,
  buyer_analysis,
  available_request_items = [],
  suppliers = [],
}: QuoteShowProps) {
  const [pdfSheetOpen, setPdfSheetOpen] = useState(false);
  const [isModalOpen, setModalOpen] = useState(false);

  const isPdf = useMemo(() => isPdfUrl(quote.pdf?.url), [quote.pdf?.url]);

  const handleMarkAsReviewed = () => {
    router.patch(quotePath(quote), {
      quote: { need_review: false },
    });
  };

  // Get set of request_item_ids that are already matched
  const matchedRequestItemIds = new Set(
    quote_items
      .filter((item) => item.request_item?.id)
      .map((item) => item.request_item!.id),
  );

  // Poll for buyer analysis if not available
  const shouldPoll = !buyer_analysis;
  usePoll(3000, {}, { autoStart: shouldPoll });

  const breadcrumbs: BreadcrumbItem[] = [
    {
      title: "Solicitudes Compras",
      href: purchaseRequestsPath(),
    },
  ];

  if (purchase_request) {
    breadcrumbs.push({
      title: `Solicitud #${purchase_request.request_number}`,
      href: purchaseRequestPath(purchase_request),
    });
  }

  const breadcrumbsWithCurrent: BreadcrumbItem[] = [
    ...breadcrumbs,
    {
      title: `Cotización ${quote.quote_number ?? `#${quote.id}`}`,
      href: "#",
    },
  ];

  return (
    <AppLayout breadcrumbs={breadcrumbsWithCurrent}>
      <Head
        title={`Cotización ${quote.quote_number ?? `#${quote.id}`} - ${quote.supplier.name}`}
      />

      <div className="flex h-full flex-1 flex-col gap-6 p-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <Button variant="ghost" size="sm" asChild className="mb-3">
              <Link
                href={
                  purchase_request
                    ? purchaseRequestPath(purchase_request)
                    : purchaseRequestsPath()
                }
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                {purchase_request
                  ? "Volver a Solicitud"
                  : "Volver a Solicitudes"}
              </Link>
            </Button>
            <h1 className="text-3xl font-bold">
              Cotización {quote.quote_number ?? `#${quote.id}`}
            </h1>
            <div className="flex items-center gap-3">
              <p className="text-lg text-muted-foreground">
                {quote.supplier.name}
              </p>
              <div className="flex gap-2">
                {quote.need_review && (
                  <Badge color="red" variant="fancy" className="text-xs">
                    <AlertTriangle className="w-3 h-3 mr-1" />
                    Necesita revisión
                  </Badge>
                )}
                {quote.missing_items_count && quote.missing_items_count > 0 && (
                  <Badge
                    variant="secondary"
                    className="text-xs bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200"
                  >
                    <AlertTriangle className="w-3 h-3 mr-1" />
                    Faltan {quote.missing_items_count} productos
                  </Badge>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {quote.need_review && (
              <Button
                onClick={() => handleMarkAsReviewed()}
                variant="default"
                className="bg-green-50 border-green-200 text-green-700 hover:bg-green-100 dark:bg-green-950 dark:border-green-800 dark:text-green-300"
              >
                <Check className="h-4 w-4 mr-2" />
                Marcar como revisada
              </Button>
            )}
            {quote.pdf?.url && (
              <Sheet open={pdfSheetOpen} onOpenChange={setPdfSheetOpen}>
                <SheetTrigger asChild>
                  <Button variant="outline">
                    <ExternalLink className="h-4 w-4 mr-2" />
                    Ver documento
                  </Button>
                </SheetTrigger>
                <SheetContent className="w-[90vw] sm:max-w-[90vw]" side="right">
                  <SheetHeader>
                    <SheetTitle>
                      Cotización {quote.quote_number ?? `#${quote.id}`}
                    </SheetTitle>
                  </SheetHeader>
                  <div className="px-4 pb-4 flex-1 min-h-0 overflow-hidden">
                    {isPdf ? (
                      <iframe
                        src={quote.pdf.url}
                        className="w-full h-full border-0 rounded-lg"
                        title={`Documento Cotización ${quote.quote_number}`}
                      />
                    ) : (
                      <div className="flex h-full w-full flex-col">
                        <div className="mb-2">
                          <a
                            href={quote.pdf.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs underline text-blue-600 hover:text-blue-700"
                          >
                            Ver imagen completa
                          </a>
                        </div>
                        <div className="flex-1 min-h-0 overflow-auto flex items-center justify-center">
                          <img
                            src={quote.pdf.url}
                            alt={`Documento de cotización ${quote.quote_number ?? `#${quote.id}`}`}
                            className="max-w-full max-h-full object-contain rounded-md shadow-sm"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </SheetContent>
              </Sheet>
            )}
          </div>
        </div>

        {/* Quote Information Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Información de la Cotización
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                {suppliers.length > 0 ? (
                  <InlineEditableCombobox
                    label="Proveedor"
                    value={quote.supplier?.id}
                    field="supplier_id"
                    updateUrl={quotePath(quote)}
                    icon={Building2}
                    options={suppliers.map((s) => ({
                      label: s.name,
                      value: s.id,
                    }))}
                    formatValue={() => quote.supplier?.name || "No asignado"}
                    searchPlaceholder="Buscar proveedor..."
                    emptyMessage="No se encontró ningún proveedor"
                  />
                ) : (
                  <div className="flex items-start gap-3">
                    <Building2 className="h-5 w-5 text-muted-foreground mt-0.5" />
                    <div>
                      <p className="text-sm font-medium">Proveedor</p>
                      <p className="text-sm text-muted-foreground">
                        {quote.supplier?.name}
                      </p>
                    </div>
                  </div>
                )}

                <InlineEditableField
                  label="Fecha de Cotización"
                  value={quote.quote_date}
                  field="quote_date"
                  type="date"
                  updateUrl={quotePath(quote)}
                  icon={Calendar}
                  formatValue={(value) =>
                    value ? String(value) : "No especificada"
                  }
                />

                {quote.valid_until && (
                  <div className="flex items-start gap-3">
                    <Calendar className="h-5 w-5 text-muted-foreground mt-0.5" />
                    <div>
                      <p className="text-sm font-medium">Válida Hasta</p>
                      <p className="text-sm text-muted-foreground">
                        {quote.valid_until}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              <div className="space-y-4">
                <InlineEditableField
                  label="Moneda"
                  value={quote.currency}
                  field="currency"
                  type="select"
                  updateUrl={quotePath(quote)}
                  icon={DollarSign}
                  options={[
                    { label: "ARS - Pesos Argentinos", value: "ARS" },
                    { label: "USD - Dólares", value: "USD" },
                  ]}
                />

                {quote.currency === "USD" && (
                  <InlineEditableField
                    label="Tipo de Cambio"
                    value={quote.exchange_rate}
                    field="exchange_rate"
                    type="number"
                    updateUrl={quotePath(quote)}
                    icon={DollarSign}
                    placeholder="Ingrese tipo de cambio USD → ARS"
                    formatValue={(value) =>
                      value
                        ? `1 USD = ${Number(value).toLocaleString("es-AR", { minimumFractionDigits: 2, maximumFractionDigits: 4 })} ARS`
                        : "No especificado"
                    }
                  />
                )}
                {quote.currency === "USD" && !quote.exchange_rate && (
                  <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm">
                    <div className="flex items-center text-amber-600">
                      <svg
                        className="h-4 w-4 mr-1"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                          clipRule="evenodd"
                        />
                      </svg>
                      Sin tipo de cambio configurado
                    </div>
                    <span className="text-amber-700">
                      Los precios no se pueden comparar correctamente sin el
                      tipo de cambio
                    </span>
                  </div>
                )}

                <div className="space-y-3">
                  <InlineEditableField
                    label="Subtotal"
                    value={quote.subtotal}
                    field="subtotal"
                    type="number"
                    updateUrl={quotePath(quote)}
                    placeholder="0.00"
                    formatValue={(value) =>
                      {
                        const base = formatCurrency(value ? Number(value) : 0, quote.currency);
                        return quote.currency === 'USD' ? `${base} USD` : base;
                      }
                    }
                  />
                  <InlineEditableField
                    label="IVA %"
                    value={quote.tax_percentage}
                    field="tax_percentage"
                    type="number"
                    updateUrl={quotePath(quote)}
                    placeholder="21.0"
                    formatValue={(value) =>
                      value !== undefined && value !== null ? `${value}%` : "21%"
                    }
                  />
                  <InlineEditableField
                    label="Impuestos"
                    value={quote.tax_amount}
                    field="tax_amount"
                    type="number"
                    updateUrl={quotePath(quote)}
                    placeholder="0.00"
                    formatValue={(value) =>
                      {
                        const base = formatCurrency(value ? Number(value) : 0, quote.currency);
                        return quote.currency === 'USD' ? `${base} USD` : base;
                      }
                    }
                  />
                  <Separator />
                  <div className="mt-2">
                    <InlineEditableField
                      label="Total"
                      value={quote.total_amount}
                      field="total_amount"
                      type="number"
                      updateUrl={quotePath(quote)}
                      icon={DollarSign}
                      placeholder="0.00"
                      formatValue={(value) =>
                        {
                          const base = formatCurrency(value ? Number(value) : 0, quote.currency);
                          return quote.currency === 'USD' ? `${base} USD` : base;
                        }
                      }
                    />
                  </div>
                  {quote.currency === "USD" && quote.total_in_ars && (
                    <div className="flex justify-between">
                      <span className="text-sm text-muted-foreground">
                        En ARS:
                      </span>
                      <span className="text-sm text-muted-foreground">
                        {formatCurrency(quote.total_in_ars, "ARS")}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Payment and Delivery Terms Section */}
            <div className="mt-6 pt-6 border-t">
              <h3 className="text-sm font-semibold text-muted-foreground mb-4">
                Condiciones Comerciales
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <InlineEditableField
                    label="Forma de Pago"
                    value={quote.payment_method}
                    field="payment_method"
                    type="text"
                    updateUrl={quotePath(quote)}
                    icon={DollarSign}
                    placeholder="Ej: Contado, Cheque, Transferencia"
                    formatValue={(value) =>
                      value ? String(value) : "No especificada"
                    }
                  />

                  <InlineEditableField
                    label="Plazo de Pago (días)"
                    value={quote.payment_term_days}
                    field="payment_term_days"
                    type="text"
                    updateUrl={quotePath(quote)}
                    icon={Clock}
                    placeholder="Ej: 30, 30-60-90 para múltiples plazos"
                    formatValue={(value) =>
                      value ? `${value} días` : "No especificado"
                    }
                  />
                </div>

                <div className="space-y-4">
                  <InlineEditableField
                    label="Plazo de Entrega"
                    value={quote.delivery_time}
                    field="delivery_time"
                    type="text"
                    updateUrl={quotePath(quote)}
                    icon={Package}
                    placeholder="Ej: Inmediato, 7 días hábiles"
                    formatValue={(value) =>
                      value ? String(value) : "No especificado"
                    }
                  />

                  <InlineEditableField
                    label="Validez de la Oferta"
                    value={quote.validity_text}
                    field="validity_text"
                    type="text"
                    updateUrl={quotePath(quote)}
                    icon={Calendar}
                    placeholder="Ej: 30 días, Hasta agotar stock"
                    formatValue={(value) =>
                      value ? String(value) : "No especificada"
                    }
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Quote Context Notes */}
        {quote.context_notes && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Condiciones Generales y Notas
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm whitespace-pre-wrap text-muted-foreground break-words max-w-prose">
                {quote.context_notes}
              </p>
            </CardContent>
          </Card>
        )}

        {/* Buyer Observations */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5" />
              Observaciones del Comprador
            </CardTitle>
            <CardDescription>
              Estas observaciones aparecerán en el PDF de la orden de compra exportada.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <InlineEditableField
              label="Observaciones"
              value={quote.buyer_notes}
              field="buyer_notes"
              type="textarea"
              updateUrl={quotePath(quote)}
              placeholder="Escribe aquí las observaciones que quieres incluir en la orden de compra (ej: 'Coordinar entrega con 48hs de anticipación')"
            />
          </CardContent>
        </Card>

        {/* Quote Items */}
        <Card className="h-full min-h-[400px]">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Package className="h-5 w-5" />
                  Items Cotizados
                </CardTitle>
                <CardDescription>
                  Haz clic en cualquier campo para editarlo. Enter guarda, Esc
                  cancela.
                </CardDescription>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setModalOpen(true)}
                disabled={available_request_items.length === 0}
                className="flex items-center gap-2"
                title={
                  available_request_items.length === 0
                    ? "No hay ítems disponibles en la solicitud de compra para agregar"
                    : undefined
                }
              >
                <Plus className="h-4 w-4" />
                Agregar ítem
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="mb-4">
              <CurrencyFormatHint />
            </div>
            <QuoteItemsGrid
              quoteItems={quote_items}
              quote={quote}
              availableRequestItems={available_request_items}
            />
          </CardContent>
        </Card>

        {/* Buyer Analysis Section */}
        {buyer_analysis ? (
          <div className="mt-8">
            <QuoteBuyerAnalysis data={buyer_analysis} />
          </div>
        ) : (
          <Card className="mt-8">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Loader2 className="h-5 w-5 animate-spin" />
                Generando Análisis para Compradores
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">
                Nuestro sistema IA está analizando la cotización para generar
                insights estratégicos...
              </p>
            </CardContent>
          </Card>
        )}

        {/* Technical Processing Information - Collapsible */}
        {(quote.confidence ?? quote.confidence_notes) && (
          <Accordion type="single" collapsible className="w-full mt-8">
            <AccordionItem value="processing-info">
              <AccordionTrigger className="text-sm text-muted-foreground hover:text-foreground">
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4" />
                  Información Técnica de Procesamiento
                </div>
              </AccordionTrigger>
              <AccordionContent>
                <Card>
                  <CardContent className="pt-6">
                    <div className="space-y-4">
                      {quote.confidence && (
                        <div className="space-y-2">
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium">
                              Confianza de extracción
                            </span>
                            <span className="text-sm font-medium">
                              {Math.round((quote.confidence || 0) * 100)}% -{" "}
                              {getConfidenceLabel(quote.confidence)}
                            </span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div
                              className={`h-2 rounded-full transition-all duration-300 ${getConfidenceColor(quote.confidence).bg}`}
                              style={{
                                width: `${(quote.confidence || 0) * 100}%`,
                              }}
                            />
                          </div>
                        </div>
                      )}

                      {quote.confidence_notes && (
                        <div className="space-y-2">
                          <span className="text-sm font-medium">
                            Notas de revisión
                          </span>
                          <div className="p-3 bg-muted/30 border rounded-lg">
                            <p className="text-sm text-muted-foreground">
                              {quote.confidence_notes}
                            </p>
                          </div>
                        </div>
                      )}

                      <div className="pt-2 border-t">
                        <span className="text-xs text-muted-foreground">
                          Procesado automáticamente con IA • Solo para
                          desarrolladores
                        </span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        )}
        
        {/* Modal para agregar nuevos ítems */}
        <NewQuoteItemModal
          open={isModalOpen}
          onOpenChange={setModalOpen}
          quote_id={quote.id}
          availableRequestItems={available_request_items}
          matchedRequestItemIds={matchedRequestItemIds}
        />
      </div>
    </AppLayout>
  );
}
